export function getUniqueCodes (codes) {
  if (!Array.isArray(codes)) {
    return []
  }
  return [...new Set(codes
    .map(code => (typeof code === 'string' ? code.trim() : code))
    .filter(Boolean))]
}

function buildPlayServiceMappings (catalog) {
  const partnerCodes = new Set()
  const planToPartner = new Map()
  const addonToPartner = new Map()
  if (!Array.isArray(catalog)) {
    return { partnerCodes, planToPartner, addonToPartner }
  }

  catalog.forEach((partner) => {
    const partnerCode = partner?.code
    if (!partnerCode) {
      return
    }
    partnerCodes.add(partnerCode)

    if (Array.isArray(partner?.plans)) {
      partner.plans.forEach((plan) => {
        if (plan?.code) {
          planToPartner.set(plan.code, partnerCode)
        }
      })
    }

    const partnerAddons = partner?.addons ?? partner?.addOns
    if (Array.isArray(partnerAddons)) {
      partnerAddons.forEach((addon) => {
        const addonCode = typeof addon === 'string' ? addon : addon?.code
        if (addonCode) {
          addonToPartner.set(addonCode, partnerCode)
        }
      })
    }
  })

  return { partnerCodes, planToPartner, addonToPartner }
}

function resolveAddonPartnerCode (addonCode, partnerCodes, addonToPartner) {
  if (addonToPartner.has(addonCode)) {
    return addonToPartner.get(addonCode)
  }
  const sortedPartners = [...partnerCodes].sort((a, b) => b.length - a.length)
  return sortedPartners.find(partnerCode =>
    addonCode === partnerCode || addonCode.startsWith(`${partnerCode}_`)
  ) ?? null
}

function groupAddonsByPartner (addonCodes, partnerCodes, addonToPartner) {
  const addonsByPartner = new Map()
  getUniqueCodes(addonCodes).forEach((addonCode) => {
    const partnerCode = resolveAddonPartnerCode(addonCode, partnerCodes, addonToPartner)
    if (!partnerCode) {
      return
    }
    const existing = addonsByPartner.get(partnerCode) || []
    existing.push(addonCode)
    addonsByPartner.set(partnerCode, existing)
  })
  return addonsByPartner
}

function attachAddonsToServices (services, addonsByPartner) {
  const remaining = new Map(addonsByPartner)
  const withAddons = services.map((entry) => {
    const addons = remaining.get(entry.partnerCode)
    if (!addons?.length) {
      return entry
    }
    remaining.delete(entry.partnerCode)
    return { ...entry, addons }
  })

  remaining.forEach((addons, partnerCode) => {
    if (addons.length) {
      withAddons.push({ planCode: null, partnerCode, addons })
    }
  })

  return withAddons
}

/**
 * Map PlayPoList codes (+ optional partner addon codes) to validate payload shape.
 * @returns {Array<{ partnerCode: string, planCode: string|null, addons?: string[] }>}
 */
export function mapCodesToAddedPlayAddOnServices (mixCodes, catalog, addonCodes = []) {
  const codes = getUniqueCodes(mixCodes)
  const { partnerCodes, planToPartner, addonToPartner } = buildPlayServiceMappings(catalog)
  const selectedPartners = new Set(codes.filter(code => partnerCodes.has(code)))
  const selectedPlans = codes
    .filter(code => planToPartner.has(code))
    .map(planCode => ({
      planCode,
      partnerCode: planToPartner.get(planCode)
    }))

  const partnersWithoutPlan = [...selectedPartners]
    .filter(partnerCode => !selectedPlans.some(plan => plan.partnerCode === partnerCode))
    .map(partnerCode => ({
      planCode: null,
      partnerCode
    }))

  const services = [...selectedPlans, ...partnersWithoutPlan]
  const addonsByPartner = groupAddonsByPartner(addonCodes, partnerCodes, addonToPartner)
  if (!addonsByPartner.size) {
    return services
  }
  return attachAddonsToServices(services, addonsByPartner)
}
